import { ANALYTICS_PAGE_BASE, TAB_ANALYTICS_NAME } from './my-ads.constants';
import type { TTabId } from './my-ads.types';
import type {
	TAnalyticsEvent,
	TCheckoutCartInput,
	TCheckoutConversionInput,
	TDataLayerAd,
	TUpgradePlanInput
} from '@/hooks/analytics';

/**
 * Builders dos eventos de analytics da página Meus Anúncios, em paridade com a
 * garagem (HOC `withAdobeLaunch` + componentes do HomeRefactor). Centralizados
 * aqui porque são disparados em mais de um lugar (página + modal de upgrade).
 */

/**
 * Pageview de uma aba em formato barra (igual garagem):
 * `/webmotors/garagem/meus-anuncios/<aba>` + pageType `meus-anuncios-<aba>`,
 * `tier: 3` (→ pageNameTier1/2/3) e os blocos site/user/page.
 */
const buildTabPageView = (tab: TTabId, recurringEvent: boolean): TAnalyticsEvent => {
	const name = TAB_ANALYTICS_NAME[tab];
	return {
		pageName: `${ANALYTICS_PAGE_BASE}/${name}`,
		pageType: `meus-anuncios-${name}`,
		flowType: 'garagem',
		use: ['site', 'user', 'page'],
		tier: 3,
		recurringEvent
	};
};

/**
 * Evento nomeado de ação no anúncio (`ativarAnuncio`, `desativarAnuncio`,
 * `excluirAnuncio`, `editarAnuncioSucesso`) — paridade com a garagem
 * (`setAdobeLaunchEvent({ ...objDataLayer, ad }, '<evento>')` / `onLaunch`).
 * Inclui os blocos site/user/page (aba atual) + ad.
 */
const buildAdEvent = (tab: TTabId, eventName: string, ad: TDataLayerAd): TAnalyticsEvent => ({
	...buildTabPageView(tab, true),
	eventName,
	use: ['site', 'user', 'page', 'ad'],
	ad
});

/**
 * Evento `turbinarAnuncio` — disparado quando o modal de upgrade carrega os
 * planos. pageName/pageType/tier fixos (upgrade-plano), com bloco `products`.
 */
const buildUpgradeEvent = (upgradePlans: ReadonlyArray<TUpgradePlanInput>): TAnalyticsEvent => ({
	eventName: 'turbinarAnuncio',
	pageName: `${ANALYTICS_PAGE_BASE}/ativos/upgrade-plano`,
	pageType: 'meus-anuncios-upgrade',
	flowType: 'garagem',
	use: ['site', 'user', 'page', 'products'],
	tier: 4,
	recurringEvent: true,
	upgradePlans
});

const CHECKOUT_PAGE_BASE = `${ANALYTICS_PAGE_BASE}/ativos`;

type TCheckoutAnalyticsParams = {
	cart: TCheckoutCartInput;
	advertId: number;
};

type TCheckoutSuccessParams = TCheckoutAnalyticsParams & {
	transactionID: string;
	paymentType: string;
	creditCardFlag?: string;
};

/**
 * Pageview do checkout upgrade (seleção de pagamento) — paridade garagem
 * `/ativos/pagamento` + blocos cart/ad.
 */
const buildCheckoutPaymentPageView = ({
	cart,
	advertId
}: TCheckoutAnalyticsParams): TAnalyticsEvent => ({
	pageName: `${CHECKOUT_PAGE_BASE}/pagamento`,
	pageType: 'meus-anuncios-upgrade-pagamento',
	flowType: 'garagem',
	use: ['site', 'user', 'page', 'cart', 'ad'],
	tier: 4,
	checkoutCart: cart,
	ad: { id: String(advertId) }
});

/**
 * Pageview da tela PIX — mantém cart/ad como no legado de pagamento upgrade.
 */
const buildCheckoutPixPageView = ({
	cart,
	advertId
}: TCheckoutAnalyticsParams): TAnalyticsEvent => ({
	pageName: `${CHECKOUT_PAGE_BASE}/pagamento`,
	pageType: 'meus-anuncios-upgrade-pagamento',
	flowType: 'garagem',
	use: ['site', 'user', 'page', 'cart', 'ad'],
	tier: 4,
	checkoutCart: cart,
	ad: { id: String(advertId) }
});

/**
 * Pageview de sucesso — cart + conversion + ad (cartão ou PIX).
 */
const buildCheckoutSuccessPageView = ({
	cart,
	advertId,
	transactionID,
	paymentType,
	creditCardFlag
}: TCheckoutSuccessParams): TAnalyticsEvent => {
	const checkoutConversion: TCheckoutConversionInput = {
		transactionID,
		paymentType
	};

	if (creditCardFlag) {
		checkoutConversion.creditCardFlag = creditCardFlag;
	}

	return {
		pageName: `${CHECKOUT_PAGE_BASE}/pagamento-sucesso`,
		pageType: 'meus-anuncios-upgrade-pagamento-sucesso',
		flowType: 'garagem',
		use: ['site', 'user', 'page', 'ad', 'cart', 'conversion'],
		tier: 4,
		checkoutCart: cart,
		ad: { id: String(advertId) },
		checkoutConversion
	};
};

export {
	buildTabPageView,
	buildAdEvent,
	buildUpgradeEvent,
	buildCheckoutPaymentPageView,
	buildCheckoutPixPageView,
	buildCheckoutSuccessPageView
};
export type { TCheckoutAnalyticsParams, TCheckoutSuccessParams };
